import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import AppRoutes from "./pages/routes";
import { ExpProvider } from "./modules/Home/ExpContext";
import { NotificationProvider } from "./modules/System/Components/Notification/NotificationManager";
import { AutoGatherProvider } from "./modules/Home/Tabs/SkillsTab/GatherTab/AutoGatherContext";
import { TabProvider } from "./modules/Home/TabProvider";
import { GatherCountProvider } from "./modules/System/Components/Gather/GatherCountContext";
import "./css/responsive.css";

function LandingAuthMusicController() {
  const location = useLocation();
  const audioRef = useRef(null);
  const shouldPlayRef = useRef(false);

  useEffect(() => {
    if (audioRef.current) return undefined;

    const audio = new Audio("/sounds/landingbg.mp3");
    audio.loop = true;
    audio.volume = 0.35;
    audioRef.current = audio;

    const tryPlay = () => {
      if (!shouldPlayRef.current) return;
      audio.play().catch(() => {});
    };

    const resumeOnGesture = () => {
      tryPlay();
      window.removeEventListener("click", resumeOnGesture);
      window.removeEventListener("keydown", resumeOnGesture);
    };

    const handleVisibility = () => {
      if (document.hidden || !shouldPlayRef.current) {
        audio.pause();
        return;
      }
      tryPlay();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("click", resumeOnGesture, { once: true });
    window.addEventListener("keydown", resumeOnGesture, { once: true });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("click", resumeOnGesture);
      window.removeEventListener("keydown", resumeOnGesture);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const pathname = String(location?.pathname || "").toLowerCase();
    const shouldPlay =
      pathname === "/" ||
      pathname === "/login" ||
      pathname === "/signup" ||
      pathname === "/admin/login";

    shouldPlayRef.current = shouldPlay;

    if (!audioRef.current) return;
    if (!shouldPlay || document.hidden) {
      audioRef.current.pause();
      return;
    }

    audioRef.current.play().catch(() => {});
  }, [location.pathname]);

  return null;
}

function App() {
  useEffect(() => {
    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      dataLayer.push(arguments);
    }
    gtag("js", new Date());
    gtag("config", "G-1GGEPC70KR");

    // Pageview tracking
    gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
    });

    // Button click tracking
    function handleButtonClick(event) {
      const button = event.target.closest("button");

      if (button) {
        const buttonId = button.id || "button-without-id";
        const buttonName =
          button.name || button.textContent || "button-without-name";

        gtag("event", "click", {
          event_category: "Button",
          event_action: "Click",
          event_label: `${buttonId} - ${buttonName}`,
        });
      }
    }

    window.addEventListener("click", handleButtonClick);

    return () => {
      window.removeEventListener("click", handleButtonClick);
    };
  }, []);

  return (
    <ExpProvider>
      <NotificationProvider>
        <GatherCountProvider>
          <AutoGatherProvider>
            <Router>
              <TabProvider>
                <div className="App">
                  <LandingAuthMusicController />
                  <AppRoutes />
                </div>
              </TabProvider>
            </Router>
          </AutoGatherProvider>
        </GatherCountProvider>
      </NotificationProvider>
    </ExpProvider>
  );
}

export default App;
